import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Modal for a customer to leave a review on a delivered booking.
 * Submission goes to the moderation queue (status=pending) and only becomes public after admin approval.
 */
export default function ReviewForm({ bookingId, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please pick a star rating");
    if (body.trim().length < 10) return toast.error("Please share a few words about your experience");
    setSubmitting(true);
    try {
      await api.post("/reviews", { booking_id: bookingId, rating, title: title.trim() || null, body: body.trim() });
      toast.success("Thanks! Your review is being reviewed by our team.");
      setOpen(false);
      setRating(0); setTitle(""); setBody("");
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`leave-review-${bookingId}`}>Leave a review</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>How was your journey?</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="flex items-center gap-1" data-testid="review-star-picker">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className="p-1"
                data-testid={`review-star-${n}`}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
              >
                <Star
                  size={28}
                  strokeWidth={1.5}
                  className={(hover || rating) >= n ? "text-amber-400" : "text-muted-foreground/40"}
                  fill={(hover || rating) >= n ? "currentColor" : "none"}
                />
              </button>
            ))}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Smooth journey to Paris" data-testid="review-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your experience</Label>
            <Textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              placeholder="How was the pick-up, journey and drop-off? Would you book again?"
              data-testid="review-body"
            />
          </div>
          <Button type="submit" disabled={submitting} className="w-full" data-testid="review-submit">
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
